import * as THREE from 'three';

const VERT = /* glsl */ `
attribute vec4 aBlob;   // x, y, z, radius
attribute float aAlpha;
varying vec2 vUv;
varying float vAlpha;
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  vAlpha = aAlpha;
  vec3 p = aBlob.xyz + vec3(position.x * aBlob.w, 0.045, position.y * aBlob.w);
  vec4 mvPosition = viewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const FRAG = /* glsl */ `
varying vec2 vUv;
varying float vAlpha;
#include <fog_pars_fragment>
void main() {
  float r = length(vUv * 2.0 - 1.0);
  float a = (1.0 - smoothstep(0.15, 1.0, r)) * 0.6 * vAlpha;
  if (a < 0.004) discard;
  gl_FragColor = vec4(0.0, 0.0, 0.0, a);
  #ifdef USE_FOG
    // Fade the darkening with distance instead of tinting it.
    #ifdef FOG_EXP2
      float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    gl_FragColor.a *= 1.0 - fogFactor;
  #endif
}`;

/**
 * Soft contact shadows under every actor, drawn as one instanced batch (the
 * classic MMO grounding blob; real shadows come on top where enabled).
 */
export class BlobShadows {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly blob: THREE.InstancedBufferAttribute;
  private readonly alpha: THREE.InstancedBufferAttribute;
  private readonly max: number;
  private n = 0;

  constructor(max = 512) {
    this.max = max;
    const base = new THREE.PlaneGeometry(2, 2);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.getAttribute('position'));
    this.geo.setAttribute('uv', base.getAttribute('uv'));
    this.blob = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.alpha = new THREE.InstancedBufferAttribute(new Float32Array(max), 1).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('aBlob', this.blob);
    this.geo.setAttribute('aAlpha', this.alpha);
    this.geo.instanceCount = 0;
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
      transparent: true,
      depthWrite: false,
      fog: true,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
    this.mesh.name = 'blob_shadows';
  }

  begin(): void {
    this.n = 0;
  }

  push(x: number, y: number, z: number, radius: number, alpha: number): void {
    if (this.n >= this.max || alpha <= 0.01) return;
    const i = this.n++;
    const b = this.blob.array as Float32Array;
    b[i * 4] = x;
    b[i * 4 + 1] = y;
    b[i * 4 + 2] = z;
    b[i * 4 + 3] = radius;
    (this.alpha.array as Float32Array)[i] = alpha;
  }

  end(): void {
    this.geo.instanceCount = this.n;
    if (this.n === 0) return;
    this.blob.clearUpdateRanges();
    this.blob.addUpdateRange(0, this.n * 4);
    this.blob.needsUpdate = true;
    this.alpha.clearUpdateRanges();
    this.alpha.addUpdateRange(0, this.n);
    this.alpha.needsUpdate = true;
  }

  dispose(): void {
    this.geo.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
